// /videos — Siggy video library. Lists curated educational videos
// from the siggy_videos table, ranked by relevance to the user's
// active medications.

import { useEffect, useState } from 'react';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { ApiError } from '../components/senior/ApiError';
import { EmptyIllustration } from '../components/senior/EmptyIllustration';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { AppPage } from '../layouts/AppPage';

interface Video {
  id: number;
  title: string;
  description: string | null;
  topics: string[];
  durationSeconds: number | null;
  matchScore: number;
  playbackUrl: string;
  thumbnailUrl: string;
}

export function Videos(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/videos');
        const json = (await res.json()) as { ok: boolean; data?: Video[] };
        if (cancelled) return;
        if (json.ok && Array.isArray(json.data)) setVideos(json.data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  return (
    <AppPage title="Siggy on video" back={{ to: '/', label: 'Home' }}>
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Watch and learn</p>
          <p className="food-intro__text">
            Short videos from Dr. Marlee Grounds on medicines you take. Two to three minutes each,
            with closed captions.
          </p>
        </div>
      </section>

      {loading ? <p style={{ padding: 'var(--space-md)' }}>Loading...</p> : null}
      {error ? (
        <ApiError
          title="Could not load videos"
          error={error}
          onRetry={() => window.location.reload()}
        />
      ) : null}

      {!loading && videos.length === 0 ? (
        <EmptyIllustration
          kind="videos"
          title="Videos coming soon"
          body="Dr. Marlee is recording the first round. Check back in a week, or set up your medicine list so we can match videos to what you take."
        />
      ) : null}

      {videos.length > 0 ? (
        <ul className="video-grid">
          {videos.map((v) => (
            <li key={v.id}>
              <a
                href={v.playbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="video-card"
              >
                <div
                  className="video-card__thumb"
                  style={{
                    backgroundImage: `url(${v.thumbnailUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <span className="video-card__play" aria-hidden="true">
                    ▶
                  </span>
                  {v.durationSeconds ? (
                    <span className="video-card__duration">
                      {formatDuration(v.durationSeconds)}
                    </span>
                  ) : null}
                </div>
                <h3 className="video-card__title">{v.title}</h3>
                {v.description ? <p className="video-card__desc">{v.description}</p> : null}
                {v.topics.length > 0 ? (
                  <div className="video-card__topics">
                    {v.topics.slice(0, 3).map((t) => (
                      <span key={t} className="safe-food-pill">
                        {t}
                      </span>
                    ))}
                  </div>
                ) : null}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </AppPage>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
